import { useLingui } from '@lingui/react/macro';
import { Outlet } from 'react-router';

import { PageTemplate } from '@/components/blocks/page-template';
import { PersistentFeedbackTab } from '@/components/feedback/persistent-feedback-tab';
import { SilentErrorBoundary } from '@/components/silent-error-boundary';
import { Toaster } from '@/components/ui/sonner';
import { useUser } from '@/lib/config/use-user';

/**
 * Layout for every authenticated route in the app. Two jobs:
 *
 * 1. **Auth gate** — `useUser()` returns the host-supplied user from
 *    `TemplateBuilderConfig` (`null` when absent). The host (WFM) owns auth and
 *    passes the user via config, so it's normally present synchronously; render
 *    a small loading spinner inside `PageTemplate` if it's missing so the shell
 *    shape doesn't jump.
 * 2. **Outlet** — once the user is present, render the matched route via
 *    `<Outlet />` inside `<PageTemplate>`.
 *
 * The host owns the app chrome (WFM's nav/header), so this no longer drives an
 * Evo app-header — that belonged to the `window.AccessEvo` MFE-host model.
 */
export function ProtectedLayout() {
  const { t } = useLingui();
  const user = useUser();

  if (!user) {
    return (
      <PageTemplate>
        <div
          role="status"
          aria-label={t`Loading`}
          className="flex h-full items-center justify-center p-12"
        >
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
        <Toaster />
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <Outlet />
      <SilentErrorBoundary>
        <PersistentFeedbackTab />
      </SilentErrorBoundary>
      <Toaster />
    </PageTemplate>
  );
}

export default ProtectedLayout;
